/**
 * 技能商店与表情包管理 API 服务
 */

import { useAuthStore } from '@/stores/auth';

export interface SkillItem {
  id: string;
  name: string;
  description: string;
  category: string;
  icon_url: string;
  author: string;
  version: string;
  version_code: number;
  always: boolean;
  access_level: 'free' | 'vip';
  tool_groups: string[];
  content_markdown: string;
  is_official: boolean;
  pinned?: boolean;
  status: 'draft' | 'published' | 'archived' | 'pending_review' | 'rejected';
  download_count: number;
  quality_score?: number;
  star_rating?: number;
  rating_count?: number;
  success_rate?: number;
  retention_rate_14d?: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface StickerItem {
  id: string;
  pack_id: string;
  file_url: string;
  thumb_url: string;
  label: string;
  meaning: string;
  keywords: string[];
  sort_order: number;
  created_at: string;
}

export interface StickerPackItem {
  id: string;
  name: string;
  style: string;
  cover_url: string;
  author: string;
  version: string;
  version_code: number;
  access_level: 'free' | 'vip';
  sticker_count: number;
  package_url: string;
  file_size: number;
  md5_checksum: string;
  is_official: boolean;
  pinned?: boolean;
  status: 'draft' | 'published' | 'archived' | 'pending_review' | 'rejected';
  download_count: number;
  quality_score?: number;
  star_rating?: number;
  rating_count?: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
  stickers?: StickerItem[];
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
}

const API_BASE = '/api';

const getHeaders = (isMultipart = false) => {
  const headers: Record<string, string> = {};
  if (!isMultipart) {
    headers['Content-Type'] = 'application/json';
  }
  const token = useAuthStore.getState().accessToken;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

export const marketApi = {
  // ======================== 技能管理 (Admin) ========================

  async getAdminSkills(params?: {
    page?: number;
    page_size?: number;
    category?: string;
    search?: string;
    status?: string;
  }): Promise<ApiResponse<PaginatedResult<SkillItem>>> {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.page_size) query.append('page_size', params.page_size.toString());
    if (params?.category) query.append('category', params.category);
    if (params?.search) query.append('search', params.search);
    if (params?.status) query.append('status', params.status);

    const res = await fetch(`${API_BASE}/admin/skills?${query.toString()}`, {
      headers: getHeaders(),
    });
    return res.json();
  },

  async createSkill(data: Partial<SkillItem>): Promise<ApiResponse<SkillItem>> {
    const res = await fetch(`${API_BASE}/admin/skills`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return res.json();
  },

  // 服务端维护的字段：编辑提交时剥离，避免整包回传导致 500（如 RFC3339 时间串写库）
  FIELD_BLACKLIST: [
    'created_at', 'updated_at', 'download_count', 'rating_count',
    'star_rating', 'quality_score', 'success_rate', 'retention_rate_14d',
  ],

  stripReadonlyFields(data: Record<string, unknown>) {
    const cleaned = { ...data };
    for (const key of this.FIELD_BLACKLIST) delete cleaned[key];
    return cleaned;
  },

  async updateSkill(id: string, data: Partial<SkillItem>): Promise<ApiResponse<SkillItem>> {
    const res = await fetch(`${API_BASE}/admin/skills/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(this.stripReadonlyFields(data as Record<string, unknown>)),
    });
    return res.json();
  },

  async updateSkillStatus(id: string, status: string): Promise<ApiResponse<SkillItem>> {
    const res = await fetch(`${API_BASE}/admin/skills/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ status }),
    });
    return res.json();
  },

  async updateSkillPin(id: string, pinned: boolean): Promise<ApiResponse<SkillItem>> {
    const res = await fetch(`${API_BASE}/admin/skills/${encodeURIComponent(id)}/pin`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ pinned }),
    });
    return res.json();
  },

  async deleteSkill(id: string): Promise<ApiResponse<any>> {
    const res = await fetch(`${API_BASE}/admin/skills/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return res.json();
  },

  async importSkillMarkdown(file: File, id?: string): Promise<ApiResponse<SkillItem>> {
    const formData = new FormData();
    formData.append('file', file);
    if (id) formData.append('id', id);

    const res = await fetch(`${API_BASE}/admin/skills/import-md`, {
      method: 'POST',
      headers: getHeaders(true),
      body: formData,
    });
    return res.json();
  },

  // ======================== 表情包管理 (Admin) ========================

  async getAdminStickers(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
  }): Promise<ApiResponse<PaginatedResult<StickerPackItem>>> {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.page_size) query.append('page_size', params.page_size.toString());
    if (params?.search) query.append('search', params.search);
    if (params?.status) query.append('status', params.status);

    const res = await fetch(`${API_BASE}/admin/stickers?${query.toString()}`, {
      headers: getHeaders(),
    });
    return res.json();
  },

  async uploadAlitaPack(file: File): Promise<ApiResponse<StickerPackItem>> {
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${API_BASE}/admin/stickers/upload-pack`, {
      method: 'POST',
      headers: getHeaders(true),
      body: formData,
    });
    return res.json();
  },

  async createBatchStickerPack(params: {
    id: string;
    name: string;
    style: string;
    author?: string;
    access_level?: string;
    stickers: Array<{
      id?: string;
      file_path: string;
      label: string;
      meaning: string;
      keywords: string[];
    }>;
  }): Promise<ApiResponse<StickerPackItem>> {
    const res = await fetch(`${API_BASE}/admin/stickers/create-batch`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(params),
    });
    return res.json();
  },

  async updateStickerPack(id: string, data: Partial<StickerPackItem>): Promise<ApiResponse<StickerPackItem>> {
    const res = await fetch(`${API_BASE}/admin/stickers/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(this.stripReadonlyFields(data as Record<string, unknown>)),
    });
    return res.json();
  },

  async updateStickerPackStatus(id: string, status: string): Promise<ApiResponse<StickerPackItem>> {
    const res = await fetch(`${API_BASE}/admin/stickers/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ status }),
    });
    return res.json();
  },

  async updateStickerPackPin(id: string, pinned: boolean): Promise<ApiResponse<StickerPackItem>> {
    const res = await fetch(`${API_BASE}/admin/stickers/${encodeURIComponent(id)}/pin`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ pinned }),
    });
    return res.json();
  },

  /**
   * 在线修正表情包内容（免整包重传）：包元数据 + 单张表情释义。
   * 服务端会自动重新打包 .alitapack 并递增 version_code。
   */
  async updateStickerPackContent(
    id: string,
    data: {
      name?: string;
      style?: string;
      access_level?: string;
      stickers?: Array<{ id: string; label?: string; meaning?: string; keywords?: string[] }>;
    }
  ): Promise<ApiResponse<StickerPackItem>> {
    const res = await fetch(`${API_BASE}/admin/stickers/${encodeURIComponent(id)}/content`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return res.json();
  },

  async deleteStickerPack(id: string): Promise<ApiResponse<any>> {
    const res = await fetch(`${API_BASE}/admin/stickers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return res.json();
  },

  // ======================== 图片上传辅助 ========================

  async uploadImage(file: File): Promise<ApiResponse<{ url: string; path: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/upload/image`, {
      method: 'POST',
      headers: getHeaders(true),
      body: formData,
    });
    return res.json();
  },

  async uploadImages(files: File[]): Promise<ApiResponse<{ urls: string[]; count: number }>> {
    const formData = new FormData();
    for (const f of files) {
      formData.append('files', f);
    }
    const res = await fetch(`${API_BASE}/upload/images`, {
      method: 'POST',
      headers: getHeaders(true),
      body: formData,
    });
    return res.json();
  },

  // ======================== 市场公共接口 ========================

  async getMarketSkills(params?: {
    page?: number;
    page_size?: number;
    category?: string;
    search?: string;
    access_level?: string;
  }): Promise<ApiResponse<PaginatedResult<SkillItem>>> {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.page_size) query.append('page_size', params.page_size.toString());
    if (params?.category) query.append('category', params.category);
    if (params?.search) query.append('search', params.search);
    if (params?.access_level) query.append('access_level', params.access_level);

    const res = await fetch(`${API_BASE}/market/skills?${query.toString()}`);
    return res.json();
  },

  async getMarketSkillDetail(id: string): Promise<ApiResponse<SkillItem>> {
    const res = await fetch(`${API_BASE}/market/skills/${encodeURIComponent(id)}`);
    return res.json();
  },

  async getMarketStickers(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    access_level?: string;
  }): Promise<ApiResponse<PaginatedResult<StickerPackItem>>> {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.page_size) query.append('page_size', params.page_size.toString());
    if (params?.search) query.append('search', params.search);
    if (params?.access_level) query.append('access_level', params.access_level);

    const res = await fetch(`${API_BASE}/market/stickers?${query.toString()}`);
    return res.json();
  },

  async getMarketStickerDetail(id: string): Promise<ApiResponse<StickerPackItem>> {
    const res = await fetch(`${API_BASE}/market/stickers/${encodeURIComponent(id)}`);
    return res.json();
  },

  async getRatings(
    targetType: 'skill' | 'sticker_pack',
    targetId: string,
    page = 1,
    pageSize = 20,
  ): Promise<ApiResponse<any>> {
    const query = new URLSearchParams({ page: page.toString(), page_size: pageSize.toString() });
    const res = await fetch(`${API_BASE}/market/${targetType}/${encodeURIComponent(targetId)}/ratings?${query.toString()}`);
    return res.json();
  },

  async submitRating(data: {
    target_type: 'skill' | 'sticker_pack';
    target_id: string;
    rating: number;
    tag?: string;
    comment?: string;
  }): Promise<ApiResponse> {
    const res = await fetch(`${API_BASE}/user/market/rate`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return res.json();
  },

  async recalculateQuality(targetType: 'skill' | 'sticker_pack', targetId: string): Promise<ApiResponse> {
    const query = new URLSearchParams({ target_type: targetType, target_id: targetId });
    const res = await fetch(`${API_BASE}/admin/metrics/recalculate?${query.toString()}`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return res.json();
  },
};
